import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { changeRequestService } from "../services/change-request.service";

export const createChangeRequest = createAsyncThunk("createChangeRequest", async (data, { rejectWithValue }) => {
    try {
        return await changeRequestService.createChangeRequest(data);
    } catch (error) {
        if (!error.response) {
            throw error;
        }
        return rejectWithValue(error.response.data);
    }
});

export const getChangeRequestList = createAsyncThunk("getChangeRequestList", async (params, { rejectWithValue }) => {
    try {
        return await changeRequestService.getChangeRequestList(params);
    } catch (error) {
        if (!error.response) {
            throw error;
        }
        return rejectWithValue(error.response.data);
    }
});

export const getChangeRequest = createAsyncThunk("getChangeRequest", async (id, { rejectWithValue }) => {
    try {
        return await changeRequestService.getChangeRequest(id);
    } catch (error) {
        if (!error.response) {
            throw error;
        }
        return rejectWithValue(error.response.data);
    }
});

export const updateChangeRequest = createAsyncThunk("updateChangeRequest", async ({id, data}, { rejectWithValue }) => {
    try {
        return await changeRequestService.updateChangeRequest(id, data);
    } catch (error) {
        if (!error.response) {
            throw error;
        }
        return rejectWithValue(error.response.data);
    }
});

export const deleteChangeRequest = createAsyncThunk("deleteChangeRequest", async (id, { rejectWithValue }) => {
    try {
        return await changeRequestService.deleteChangeRequest(id);
    } catch (error) {
        if (!error.response) {
            throw error;
        }
        return rejectWithValue(error.response.data);
    }
});

export const updateChangeRequestStatus = createAsyncThunk("updateChangeRequestStatus", async (data, { rejectWithValue }) => {
    try {
        return await changeRequestService.updateChangeRequestStatus(data);
    } catch (error) {
        if (!error.response) {
            throw error;
        }
        return rejectWithValue(error.response.data);
    }
});

export const getChangeRequestsByProject = createAsyncThunk("getChangeRequestsByProject", async (projectId, { rejectWithValue }) => {
    try {
        return await changeRequestService.getChangeRequestsByProject(projectId);
    } catch (error) {
        if (!error.response) {
            throw error;
        }
        return rejectWithValue(error.response.data);
    }
});

const changeRequestSlice = createSlice({
    name: "pmo-change-request",
    initialState: {
        list: {
            data: null,
            isLoading: false,
            isError: false,
            message: null,
        },
        detail: {
            data: null,
            isLoading: false,
            isError: false,
            message: null,
        },
        create: {
            data: null,
            isLoading: false,
            isError: false,
            message: null,
        },
        update: {
            data: null,
            isLoading: false,
            isError: false,
            message: null,
        },
        delete: {
            data: null,
            isLoading: false,
            isError: false,
            message: null,
        },
        status: {
            data: null,
            isLoading: false,
            isError: false,
            message: null,
        },
        projectRequests: {
            data: null,
            isLoading: false,
            isError: false,
            message: null,
        }
    },
    reducers: {
        resetStateChangeRequest: (state) => {
            state.list.isError = false;
            state.list.message = null;
            state.detail.isError = false;
            state.detail.message = null;
            state.create.isError = false;
            state.create.message = null;
            state.update.isError = false;
            state.update.message = null;
            state.delete.isError = false;
            state.delete.message = null;
            state.status.isError = false;
            state.status.message = null;
            state.projectRequests.isError = false;
            state.projectRequests.message = null;
        },
    },
    extraReducers: (builder) => {
        // get change request list
        builder.addCase(getChangeRequestList.pending, (state) => {
            state.list.isLoading = true;
        });
        builder.addCase(getChangeRequestList.fulfilled, (state, action) => {
            state.list.isLoading = false;
            state.list.data = action.payload;
            state.list.isError = false;
        });
        builder.addCase(getChangeRequestList.rejected, (state, action) => {
            state.list.isLoading = false;
            state.list.isError = true;
            state.list.message = action.payload?.message;
        });

        // get change request detail
        builder.addCase(getChangeRequest.pending, (state) => {
            state.detail.isLoading = true;
        });
        builder.addCase(getChangeRequest.fulfilled, (state, action) => {
            state.detail.isLoading = false;
            state.detail.data = action.payload;
            state.detail.isError = false;
        });
        builder.addCase(getChangeRequest.rejected, (state, action) => {
            state.detail.isLoading = false;
            state.detail.isError = true;
            state.detail.message = action.payload?.message;
        });

        // create change request
        builder.addCase(createChangeRequest.pending, (state) => {
            state.create.isLoading = true;
        });
        builder.addCase(createChangeRequest.fulfilled, (state, action) => {
            state.create.isLoading = false;
            state.create.data = action.payload;
            state.create.isError = false;
        });
        builder.addCase(createChangeRequest.rejected, (state, action) => {
            state.create.isLoading = false;
            state.create.isError = true;
            state.create.message = action.payload?.message;
        });

        // update change request
        builder.addCase(updateChangeRequest.pending, (state) => {
            state.update.isLoading = true;
        });
        builder.addCase(updateChangeRequest.fulfilled, (state, action) => {
            state.update.isLoading = false;
            state.update.data = action.payload;
            state.update.isError = false;
        });
        builder.addCase(updateChangeRequest.rejected, (state, action) => {
            state.update.isLoading = false;
            state.update.isError = true;
            state.update.message = action.payload?.message;
        });

        // delete change request
        builder.addCase(deleteChangeRequest.pending, (state) => {
            state.delete.isLoading = true;
        });
        builder.addCase(deleteChangeRequest.fulfilled, (state, action) => {
            state.delete.isLoading = false;
            state.delete.data = action.payload;
            state.delete.isError = false;
        });
        builder.addCase(deleteChangeRequest.rejected, (state, action) => {
            state.delete.isLoading = false;
            state.delete.isError = true;
            state.delete.message = action.payload?.message;
        });

        // update change request status
        builder.addCase(updateChangeRequestStatus.pending, (state) => {
            state.status.isLoading = true;
        });
        builder.addCase(updateChangeRequestStatus.fulfilled, (state, action) => {
            state.status.isLoading = false;
            state.status.data = action.payload;
            state.status.isError = false;
        });
        builder.addCase(updateChangeRequestStatus.rejected, (state, action) => {
            state.status.isLoading = false;
            state.status.isError = true;
            state.status.message = action.payload?.message;
        });

        // get change requests by project
        builder.addCase(getChangeRequestsByProject.pending, (state) => {
            state.projectRequests.isLoading = true;
        });
        builder.addCase(getChangeRequestsByProject.fulfilled, (state, action) => {
            state.projectRequests.isLoading = false;
            state.projectRequests.data = action.payload;
            state.projectRequests.isError = false;
        });
        builder.addCase(getChangeRequestsByProject.rejected, (state, action) => {
            state.projectRequests.isLoading = false;
            state.projectRequests.isError = true;
            state.projectRequests.message = action.payload?.message;
        });
    },
});

export const { resetStateChangeRequest } = changeRequestSlice.actions;
export default changeRequestSlice.reducer;
